/**
 * EOSIO Signing Request v1.0.4
 * amaxup
 *
 * @license
 * Copyright © 2021 Greymass Inc.
 * 
 * Permission is hereby granted, free of charge, to any person
 * obtaining a copy of this software and associated documentation
 * files (the “Software”), to deal in the Software without
 * restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the
 * Software is furnished to do so, subject to the following
 * conditions:
 * 
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 * OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
 * OTHER DEALINGS IN THE SOFTWARE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR
 * INTENDED FOR USE IN THE DESIGN, CONSTRUCTION, OPERATION OR
 * MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { __classPrivateFieldSet, __classPrivateFieldGet } from 'tslib';

class IdGenerator {
    static rand() {
        const arr = new Uint32Array(1);
        window.crypto.getRandomValues(arr);
        return arr[0] / (0xffffffff + 1);
    }
    /***
     * Generates a random string of specified size
     * @param size - The length of the string to generate
     * @returns {string} - The generated random string
     */
    static text(size) {
        let text = '';
        const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < size; i++)
            text += possible.charAt(Math.floor(IdGenerator.rand() * possible.length));
        return text;
    }
    /***
     * Generates a random number of specified size
     * @param size - The length of the number to generate
     * @returns {string} - The generated random number ( as a string )
     */
    static numeric(size) {
        const add = 1;
        let max = 12 - add;
        if (size > max)
            return IdGenerator.numeric(max) + IdGenerator.numeric(size - max);
        max = Math.pow(10, size + add);
        const min = max / 10, number = Math.floor(IdGenerator.rand() * (max - min + 1)) + min;
        return ('' + number).substring(add);
    }
}

var SourceType;
(function (SourceType) {
    SourceType["AMAX_UP_CLIENT"] = "amax_up_client";
    SourceType["AMAX_UP_SERVER"] = "amax_up_server";
})(SourceType || (SourceType = {}));
var ClientEventType;
(function (ClientEventType) {
    ClientEventType["GET_ACCOUNT"] = "getAccount";
    ClientEventType["TRANSACT"] = "transact";
    ClientEventType["SIGN_MESSAGE"] = "signMessage";
    ClientEventType["REQUEST"] = "request";
})(ClientEventType || (ClientEventType = {}));
var CommonEventType;
(function (CommonEventType) {
    CommonEventType["ERROR"] = "error";
})(CommonEventType || (CommonEventType = {}));
var ServerEventType;
(function (ServerEventType) {
    ServerEventType["ACCOUNTS_CHANGED"] = "accountsChanged";
})(ServerEventType || (ServerEventType = {}));

var _Client_instances, _Client_resolvers, _Client_events, _Client_requestRPC;
class Client {
    constructor(Win) {
        _Client_instances.add(this);
        _Client_resolvers.set(this, void 0);
        _Client_events.set(this, void 0);
        this.parent = Win || window.parent;
        this.win = window;
        __classPrivateFieldSet(this, _Client_resolvers, {}, "f");
        __classPrivateFieldSet(this, _Client_events, {}, "f");
        this.addEventListener(async (message) => {
            const resolve = __classPrivateFieldGet(this, _Client_resolvers, "f")[message.id];
            const fn = __classPrivateFieldGet(this, _Client_events, "f")[message.type];
            if (resolve) {
                if (message.type === CommonEventType.ERROR) {
                    resolve.reject(message.payload);
                }
                else {
                    resolve.resolve(message.payload);
                }
            }
            if (fn) {
                await fn(message.payload);
            }
        });
        this.rpc = {
            abi_bin_to_json: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'abi_bin_to_json'),
            abi_json_to_bin: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'abi_json_to_bin'),
            /** Raw call to `/v1/chain/get_abi` */
            get_abi: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_abi'),
            /** Raw call to `/v1/chain/get_account` */
            get_account: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_account'),
            /** Raw call to `/v1/chain/get_accounts_by_authorizers` */
            get_accounts_by_authorizers: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_accounts_by_authorizers'),
            /** Raw call to `get_activated_protocol_features` */
            get_activated_protocol_features: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_activated_protocol_features'),
            /** Raw call to `/v1/chain/get_block_header_state` */
            get_block_header_state: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_block_header_state'),
            /** Raw call to `/v1/chain/get_block_info` */
            get_block_info: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_block_info'),
            /** Raw call to `/v1/chain/get_block` */
            get_block: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_block'),
            /** Raw call to `/v1/chain/get_code` */
            get_code: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_code'),
            /** Raw call to `/v1/chain/get_code_hash` */
            get_code_hash: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_code_hash'),
            /** Raw call to `/v1/chain/get_currency_balance` */
            get_currency_balance: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_currency_balance'),
            /** Raw call to `/v1/chain/get_currency_stats` */
            get_currency_stats: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_currency_stats'),
            /** Raw call to `/v1/chain/get_info` */
            get_info: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_info'),
            /** Raw call to `/v1/chain/get_producer_schedule` */
            get_producer_schedule: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_producer_schedule'),
            /** Raw call to `/v1/chain/get_producers` */
            get_producers: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_producers'),
            /** Raw call to `/v1/chain/get_raw_code_and_abi` */
            get_raw_code_and_abi: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_raw_code_and_abi'),
            /** calls `/v1/chain/get_raw_code_and_abi` and pulls out unneeded raw wasm code */
            getRawAbi: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'getRawAbi'),
            /** Raw call to `/v1/chain/get_raw_abi` */
            get_raw_abi: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_raw_abi'),
            /** Raw call to `/v1/chain/get_scheduled_transactions` */
            get_scheduled_transactions: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_scheduled_transactions'),
            /** Raw call to `/v1/chain/get_table_rows` */
            get_table_rows: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_table_rows'),
            /** Raw call to `/v1/chain/get_kv_table_rows` */
            get_kv_table_rows: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_kv_table_rows'),
            /** Raw call to `/v1/chain/get_table_by_scope` */
            get_table_by_scope: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'get_table_by_scope'),
            /** Get subset of `availableKeys` needed to meet authorities in `transaction`. Implements `AuthorityProvider` */
            getRequiredKeys: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'getRequiredKeys'),
            /** Push a serialized transaction (replaced by send_transaction, but returned format has changed) */
            push_transaction: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'push_transaction'),
            /** Raw call to `/v1/chain/push_ro_transaction */
            push_ro_transaction: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'push_ro_transaction'),
            push_transactions: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'push_transactions'),
            /** Send a serialized transaction */
            send_transaction: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'send_transaction'),
            /** Raw call to `/v1/db_size/get` */
            db_size_get: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'db_size_get'),
            /** Raw call to `/v1/trace_api/get_block` */
            trace_get_block: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'trace_get_block'),
            /** Raw call to `/v1/history/get_actions` */
            history_get_actions: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'history_get_actions'),
            /** Raw call to `/v1/history/get_transaction` */
            history_get_transaction: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'history_get_transaction'),
            /** Raw call to `/v1/history/get_key_accounts` */
            history_get_key_accounts: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'history_get_key_accounts'),
            /** Raw call to `/v1/history/get_controlled_accounts` */
            history_get_controlled_accounts: __classPrivateFieldGet(this, _Client_instances, "m", _Client_requestRPC).call(this, 'history_get_controlled_accounts'),
        };
    }
    async request(params) {
        return await this.postMessage({
            type: ClientEventType.REQUEST,
            payload: params,
        });
    }
    async getAccount() {
        return await this.postMessage({
            type: ClientEventType.GET_ACCOUNT,
        });
    }
    async transact(actions) {
        return await this.postMessage({
            type: ClientEventType.TRANSACT,
            payload: actions,
        });
    }
    async signMessage(msg) {
        return await this.postMessage({
            type: ClientEventType.SIGN_MESSAGE,
            payload: msg,
        });
    }
    on(type, fn) {
        __classPrivateFieldGet(this, _Client_events, "f")[type] = fn;
    }
    remove(type) {
        if (type) {
            delete __classPrivateFieldGet(this, _Client_events, "f")[type];
        }
        else {
            __classPrivateFieldSet(this, _Client_events, {}, "f");
        }
    }
    addEventListener(listener) {
        this.win.addEventListener('message', (e) => {
            const msg = e.data;
            if (msg.source === SourceType.AMAX_UP_SERVER) {
                listener(msg);
            }
        });
    }
    postMessage(message) {
        return new Promise((resolve, reject) => {
            const id = IdGenerator.numeric(24);
            message.id = id;
            message.source = SourceType.AMAX_UP_CLIENT;
            __classPrivateFieldGet(this, _Client_resolvers, "f")[id] = { resolve, reject };
            this.parent.postMessage(message, '*');
        });
    }
    isInIframe() {
        return window.parent !== window;
    }
}
_Client_resolvers = new WeakMap(), _Client_events = new WeakMap(), _Client_instances = new WeakSet(), _Client_requestRPC = function _Client_requestRPC(fnName) {
    return async (...args) => {
        return await this.request({
            method: 'rpc',
            params: {
                request: {
                    method: fnName,
                    params: args,
                },
            },
        });
    };
};

var _Server_events;
class Server {
    constructor(iframe) {
        _Server_events.set(this, void 0);
        this.iframe = iframe.contentWindow;
        this.win = window;
        __classPrivateFieldSet(this, _Server_events, {}, "f");
        this.addEventListener(async (message) => {
            const fn = __classPrivateFieldGet(this, _Server_events, "f")[message.type];
            if (fn) {
                const data = (await fn(message));
                this.postMessage(data);
            }
            // switch (data.type) {
            //     case EventType.GET_ACCOUNT:
            //         {
            //             fn(data.payload)
            //         }
            //         break
            //     default:
            //         throw new Error('type is not defined')
            // }
        });
    }
    on(type, fn) {
        __classPrivateFieldGet(this, _Server_events, "f")[type] = fn;
    }
    remove(type) {
        if (type) {
            delete __classPrivateFieldGet(this, _Server_events, "f")[type];
        }
        else {
            __classPrivateFieldSet(this, _Server_events, {}, "f");
        }
    }
    addEventListener(listener) {
        this.win.addEventListener('message', (e) => {
            const msg = e.data;
            if (msg.source === SourceType.AMAX_UP_CLIENT) {
                listener(msg);
            }
        });
    }
    postMessage(message) {
        message.source = SourceType.AMAX_UP_SERVER;
        this.iframe.postMessage(message, '*');
    }
    accountsChanged(accounts) {
        this.postMessage({
            type: ServerEventType.ACCOUNTS_CHANGED,
            payload: accounts,
        });
    }
}
_Server_events = new WeakMap();

export { Client, ClientEventType, CommonEventType, Server, ServerEventType, SourceType };
//# sourceMappingURL=index.m.js.map
